import { supabase } from '@/integrations/supabase/client';

const stripQueryAndHash = (value: string) => value.split('#')[0].split('?')[0];

export const extractSupabaseStoragePath = (bucket: string, pathOrUrl: string): string | null => {
  const raw = (pathOrUrl ?? '').trim();
  if (!raw) return null;

  // If caller already stored a path like "logos/abc.png".
  if (!raw.startsWith('http://') && !raw.startsWith('https://') && !raw.startsWith('data:')) {
    return stripQueryAndHash(raw).replace(/^\/+/, '');
  }

  // If it's a Supabase public URL, e.g. ".../storage/v1/object/public/<bucket>/<path>"
  // Or signed URL, e.g. ".../storage/v1/object/sign/<bucket>/<path>?token=..."
  const base = stripQueryAndHash(raw);
  const publicMarker = `/storage/v1/object/public/${bucket}/`;
  const signedMarker = `/storage/v1/object/sign/${bucket}/`;
  const idxPublic = base.indexOf(publicMarker);
  const idxSigned = base.indexOf(signedMarker);

  if (idxPublic >= 0) return base.slice(idxPublic + publicMarker.length);
  if (idxSigned >= 0) return base.slice(idxSigned + signedMarker.length);

  return null;
};

export const resolveSignedStorageUrl = async (
  bucket: string,
  pathOrUrl: string,
  expiresInSeconds: number
): Promise<string | null> => {
  const raw = (pathOrUrl ?? '').trim();
  if (!raw) return null;

  // If it already looks like a signed Supabase URL, just use it.
  if (raw.includes(`/storage/v1/object/sign/${bucket}/`)) return raw;

  const path = extractSupabaseStoragePath(bucket, raw);
  if (!path) return null;

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresInSeconds);
  if (error) return null;
  return data?.signedUrl ?? null;
};

